import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { categoriesService } from '../services/categories';
import { Category } from '../types/woocommerce';
import { ChevronRightIcon, SparklesIcon } from '@heroicons/react/24/outline';

interface CategoryWithChildren extends Category {
  children?: CategoryWithChildren[];
}

export const CategoriesPage: React.FC = () => {
  // Récupérer toutes les catégories
  const { data: categoriesData, isLoading, error } = useQuery({
    queryKey: ['categories', 'tree'],
    queryFn: () => categoriesService.getCategoryTree(),
  });

  if (isLoading) {
    return (
      <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center mb-12">
            <div className="h-10 w-64 bg-gray-200 rounded mx-auto mb-4 animate-pulse"></div>
            <div className="h-6 w-96 bg-gray-200 rounded mx-auto animate-pulse"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm animate-pulse">
                <div className="h-56 bg-gradient-to-br from-gray-200 to-gray-300"></div>
                <div className="p-6">
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <h1 className="text-4xl font-bold text-center mb-8">Nos Catégories</h1>
          <div className="text-center py-12 bg-white rounded-xl shadow-sm">
            <p className="text-red-500">Erreur lors du chargement des catégories</p>
          </div>
        </div>
      </div>
    );
  }

  const renderCategoryCard = (category: CategoryWithChildren) => {
    const hasChildren = category.children && category.children.length > 0;
    
    return (
      <div key={category.id} className="group relative bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
        <Link to={`/category/${category.slug}`} className="block">
          {/* Image ou placeholder */}
          <div className="relative overflow-hidden">
            {category.image ? (
              <>
                <img
                  src={category.image.src}
                  alt={category.image.alt || category.name}
                  className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </>
            ) : (
              <div className="w-full h-56 bg-gradient-to-br from-[#497aab] via-[#5a8bc4] to-[#c69c70] flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
                <span className="relative text-white text-5xl font-light tracking-wider">
                  {category.name.charAt(0)}
                </span>
                <SparklesIcon className="absolute top-4 right-4 h-8 w-8 text-white/50" />
              </div>
            )}
            
            {/* Badge compteur */}
            <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full shadow-lg">
              <span className="text-sm font-medium text-[#497aab]">
                {category.count} bijou{category.count > 1 ? 'x' : ''}
              </span>
            </div>
          </div>
          
          {/* Contenu */}
          <div className="p-6">
            <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-[#c69c70] transition-colors duration-300">
              {category.name}
            </h3>
            
            {category.description && (
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {category.description}
              </p>
            )}
            
            <div className="flex items-center text-[#497aab] font-medium text-sm group-hover:text-[#c69c70] transition-colors">
              <span>Découvrir</span>
              <ChevronRightIcon className="h-4 w-4 ml-1 transform group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </Link>
        
        {/* Sous-catégories */}
        {hasChildren && (
          <div className="px-6 pb-6 pt-2 border-t border-gray-100">
            <div className="flex flex-wrap gap-2">
              {category.children!.slice(0, 3).map((child) => (
                <Link
                  key={child.id}
                  to={`/category/${child.slug}`}
                  className="text-xs bg-gray-50 hover:bg-[#c69c70] hover:text-white px-3 py-1.5 rounded-full transition-all duration-200 border border-gray-200 hover:border-[#c69c70]"
                >
                  {child.name}
                </Link>
              ))}
              {category.children!.length > 3 && (
                <span className="text-xs text-gray-500 px-3 py-1.5 flex items-center">
                  +{category.children!.length - 3}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const categories = categoriesData || [];

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#497aab] to-[#5a8bc4] text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-light mb-4 tracking-wide">Nos Collections</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto font-light">
            Découvrez nos bijoux en or recyclé, créés avec passion et savoir-faire
          </p>
          <div className="mt-8 flex items-center justify-center gap-2">
            <div className="h-px w-12 bg-[#c69c70]"></div>
            <SparklesIcon className="h-6 w-6 text-[#c69c70]" />
            <div className="h-px w-12 bg-[#c69c70]"></div>
          </div>
        </div>
      </div>

      {/* Catégories Grid */}
      <div className="container mx-auto px-4 py-16">
        {categories.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm">
            <p className="text-gray-500">Aucune catégorie disponible pour le moment</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {categories.map(renderCategoryCard)}
          </div>
        )}
      </div>
    </div>
  );
};

